import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { FaFileAlt } from 'react-icons/fa'
import Divider from './Divider'

interface MinutesData {
  allMdx: {
    nodes: Array<{
      id: string
      frontmatter: {
        title: string
        date: string
        attachment: string
      }
    }>
  }
}

const MinutesList: React.FC = () => {
  const data = useStaticQuery<MinutesData>(graphql`
    query MinutesListQuery {
      allMdx(
        filter: { internal: { contentFilePath: { regex: "/content/minutes/" } } }
        sort: { frontmatter: { date: DESC } }
      ) {
        nodes {
          id
          frontmatter {
            title
            date
            attachment
          }
        }
      }
    }
  `)

  const minutes = data.allMdx.nodes

  // Group minutes by year
  const groupedMinutes = minutes.reduce((acc, minute) => {
    const year = new Date(minute.frontmatter.date).getFullYear()
    if (!acc[year]) {
      acc[year] = []
    }
    acc[year].push(minute)
    return acc
  }, {} as Record<number, typeof minutes>)

  const years = Object.keys(groupedMinutes).map(Number).sort((a, b) => b - a)

  if (minutes.length === 0) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-semibold text-text-light mb-4">
          No minutes found
        </h2>
        <p className="text-text-light">
          Meeting minutes will appear here once they're published.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {years.map((year) => (
        <section key={year}>
          <h2 className="text-2xl font-heading font-bold text-primary mb-4">
            {year}
          </h2>
          <div className="space-y-3">
            {groupedMinutes[year].map((minute) => {
              return (
                <a
                  href={minute.frontmatter.attachment}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={minute.id}
                  className="block mb-4"
                >
                  <article className="bg-gray-50 rounded-lg px-4 py-6 hover:bg-gray-100 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 flex items-start gap-4">
                        <div className="text-primary text-2xl mt-1">
                          <FaFileAlt />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg md:text-xl mb-2 mt-0">
                            {minute.frontmatter.title}
                          </h3>
                          <div className="flex flex-wrap items-baseline gap-2 text-sm text-text-light">
                            <span>
                              {new Date(minute.frontmatter.date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-primary font-medium text-sm whitespace-nowrap self-center">
                        View PDF →
                      </div>
                    </div>
                  </article>
                </a>
              )
            })}
          </div>
          <Divider />
        </section>
      ))}
    </div>
  )
}

export default MinutesList
